/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import {
  AppIds,
  CompanyTrackerIds,
  CompanyTrackerLightIds,
  PortfolioTrackerIds
} from '../utils/permission-groups';
import { generateUrl } from './util';
import { ROUTES } from '../constants/routes';
import { InitiativeType, RootInitiativeData } from '../types/initiative';
import { SettingStorage } from '../services/SettingStorage';

export interface AppRootMatcher {
  storageKey: string;
  filterInitiatives: <T extends Pick<RootInitiativeData, 'permissionGroup' | 'type'>>(initiatives: T[]) => T[];
  generateUrl: (id: string, rootAppPath?: string) => string;
}

export const companyTrackerRouteMatcher: AppRootMatcher = {
  storageKey: AppIds.CompanyTracker,
  filterInitiatives: (initiatives) => initiatives.filter((r) => {
    return [
      ...CompanyTrackerIds,
      ...CompanyTrackerLightIds
    ].includes(r.permissionGroup as string);
  }),
  generateUrl: (id: string, rootAppPath) => generateUrl(ROUTES.COMPANY_TRACKER_SURVEY_REDIRECTOR, {
    rootAppPath: rootAppPath || 'company-tracker',
    initiativeId: id
  }),
}

export const materialityAssessmentRouteMatcher: AppRootMatcher = {
  storageKey: AppIds.MaterialityAssessment,
  filterInitiatives: companyTrackerRouteMatcher.filterInitiatives,
  generateUrl: (id: string) => generateUrl(ROUTES.MATERIALITY_ASSESSMENT, {
    initiativeId: id
  }),
}

export const portfolioTrackerRouteMatcher: AppRootMatcher = {
  storageKey: AppIds.PortfolioTracker,
  filterInitiatives: (initiatives) => initiatives.filter((r) => {
    return PortfolioTrackerIds.includes(r.permissionGroup as string) && r.type === InitiativeType.initiativeGroup;
  }),
  generateUrl: (id: string) => generateUrl(id ? ROUTES.PORTFOLIO_TRACKER_PORTFOLIO : ROUTES.PORTFOLIO_TRACKER, { portfolioId: id }),
}

export const assuranceRouteMatcher: AppRootMatcher = {
  storageKey: AppIds.Assurance,
  filterInitiatives: () => [],
  generateUrl: () => generateUrl(ROUTES.ASSURANCE),
}

const supportedApps = {
  [AppIds.CompanyTracker]: companyTrackerRouteMatcher,
  [AppIds.CompanyTrackerLight]: companyTrackerRouteMatcher,
  [AppIds.PortfolioTracker]: portfolioTrackerRouteMatcher,
  [AppIds.Assurance]: assuranceRouteMatcher,
  [AppIds.MaterialityAssessment]: materialityAssessmentRouteMatcher,
}

export const navigateToRootApp = (initiatives: RootInitiativeData[], appId: keyof typeof supportedApps) => {
  const matcher = supportedApps[appId];
  const roots = matcher.filterInitiatives(initiatives);

  const selectedId = SettingStorage.getItem(matcher.storageKey);
  const rootInitiative = roots.find((o) => o._id === selectedId) ?? roots[0];

  return matcher.generateUrl(rootInitiative?._id ?? '');
}
